// Paying from several Jupnet stablecoins at once: a USD basket (architecture §14). The open
// escrows each one; on the wire the intent names one nominal input, the basket marker, worth the
// stablecoins' sum in 6-decimal USD. The api takes `inputs: [{token, amount}]`; with one entry it
// is an ordinary single-stablecoin open.

import type { Token } from "./chains";
import { nativeTokenAddr } from "./wire";

/** The program-derived marker `["usd_basket"]` of the Jupnet program: a basket's `input_token`. */
export const BASKET_TOKEN = "2ud1JSUEToraEQENetrLNVQg6WPyzdXD5z5fQWkw2BtD";
/** The most stablecoins one open takes (`MAX_BASKET_COMPONENTS`: every Jupnet stablecoin). */
export const BASKET_MAX = 6;
/** How a basket's amounts read: 6-decimal USD. */
export const USD: Token = { sym: "USD", name: "USD (basket)", addr: BASKET_TOKEN, decimals: 6 };

/** One stablecoin of the basket and its amount in smallest units (null while not a valid amount). */
export type BasketPart = { token: Token; amount: string | null };

/** A gross in `decimals` floored to 6-decimal USD at par, as the program values it. */
export function usdOf(amount: bigint, decimals: number): bigint {
  return decimals >= 6 ? amount / 10n ** BigInt(decimals - 6) : amount * 10n ** BigInt(6 - decimals);
}

/** The basket's value in 6-decimal USD (the parts with an amount). */
export const basketUsd = (parts: BasketPart[]): bigint => parts.reduce((n, p) => n + (p.amount ? usdOf(BigInt(p.amount), p.token.decimals) : 0n), 0n);

/** `/quote`'s `inputs`, or null when a part has no amount, a part is worth under a micro-USD (the
 *  program refuses it), a stablecoin repeats, or there are more than `BASKET_MAX`. */
export function basketInputs(parts: BasketPart[]): { token: string; amount: string }[] | null {
  if (!parts.length || parts.length > BASKET_MAX) return null;
  if (new Set(parts.map((p) => p.token.addr)).size !== parts.length) return null;
  if (parts.some((p) => !p.amount || usdOf(BigInt(p.amount), p.token.decimals) === 0n)) return null;
  return parts.map((p) => ({ token: p.token.addr, amount: p.amount! }));
}

/** Whether an intent's `input_token` (wire hex) is the basket marker. */
export const isBasketRow = (inputToken: string): boolean => nativeTokenAddr(inputToken, "jupnet") === BASKET_TOKEN;

/** The stablecoins a new row may add: every one not yet in the basket. */
export const addable = (all: Token[], parts: { addr: string }[]): Token[] => all.filter((t) => !parts.some((p) => p.addr === t.addr));

/** A basket row as the form holds it: a stablecoin by address and the amount typed. */
export type BasketRow = { addr: string; amount: string };

/** Each row with its stablecoin, kept in step: a row whose stablecoin is no longer listed is
 *  dropped explicitly (never by an index drifting between two arrays), so no amount is ever paired
 *  with another row's mint. */
export function pairRows(rows: BasketRow[], all: Token[]): { token: Token; amount: string }[] {
  return rows.flatMap((r) => {
    const token = all.find((t) => t.addr === r.addr);
    return token ? [{ token, amount: r.amount }] : [];
  });
}
